/**
 * Data Sync Validator
 *
 * Validates that Redux state matches server state and triggers
 * refetch when data is stale or out of sync.
 */
import store from '../redux/store';
import axiosInstance from './axios-auth';
class DataSyncValidator {
    constructor() {
        this.syncStatus = new Map();
        this.validationInterval = null;
        this.STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
        this.VALIDATION_INTERVAL = 2 * 60 * 1000; // 2 minutes
        this.listeners = [];
        /** Queued stale data types when no subscriber is listening (e.g. provider unmounted). Flushed when a listener is added. */
        this.queuedStaleDataTypes = new Set();
    }
    static getInstance() {
        if (!DataSyncValidator.instance) {
            DataSyncValidator.instance = new DataSyncValidator();
        }
        return DataSyncValidator.instance;
    }
    /**
     * Start automatic validation
     */
    startValidation() {
        if (this.validationInterval) {
            clearInterval(this.validationInterval);
        }
        this.validationInterval = setInterval(() => {
            this.validateAllData();
        }, this.VALIDATION_INTERVAL);
    }
    /**
     * Stop automatic validation
     */
    stopValidation() {
        if (this.validationInterval) {
            clearInterval(this.validationInterval);
            this.validationInterval = null;
        }
    }
    /**
     * Validate all data types
     */
    async validateAllData() {
        const result = {
            isValid: true,
            staleData: [],
            errors: [],
            warnings: []
        };
        try {
            const state = store.getState();
            // Validate different data types
            await Promise.all([
                this.validateAuthData(state, result),
                this.validateLessonsData(state, result),
                this.validateNotesData(state, result),
                this.validateQuizData(state, result),
                this.validateScheduleData(state, result)
            ]);
            // Update sync status
            result.staleData.forEach(status => {
                this.syncStatus.set(status.dataType, status);
            });
            // Notify listeners
            this.listeners.forEach(listener => listener(result));
            // Log results
            if (result.staleData.length > 0) {
                console.warn('⚠️ DataSyncValidator: Found stale data:', result.staleData);
            }
            if (result.errors.length > 0) {
                console.error('❌ DataSyncValidator: Validation errors:', result.errors);
                result.isValid = false;
            }
        }
        catch (error) {
            console.error('❌ DataSyncValidator: Validation failed:', error);
            result.isValid = false;
            result.errors.push(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        return result;
    }
    /**
     * Validate auth data
     */
    async validateAuthData(state, result) {
        try {
            const auth = state.auth;
            if (!auth.user || !auth.isAuthenticated)
                return;
            const lastSync = this.getLastSyncTime('auth');
            const isStale = Date.now() - lastSync > this.STALE_THRESHOLD;
            if (isStale) {
                // Check server timestamp
                const serverTimestamp = await this.getServerTimestamp('/auth/verify');
                result.staleData.push({
                    isStale: true,
                    lastSync,
                    serverTimestamp,
                    clientTimestamp: Date.now(),
                    dataType: 'auth',
                    needsRefresh: true
                });
            }
        }
        catch (error) {
            result.errors.push(`Auth validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Validate lessons data
     */
    async validateLessonsData(state, result) {
        try {
            const lessons = state.lessons;
            if (!lessons.items || lessons.items.length === 0)
                return;
            const lastSync = this.getLastSyncTime('lessons');
            const isStale = Date.now() - lastSync > this.STALE_THRESHOLD;
            if (isStale) {
                // Check if we have recent lessons
                const hasRecentLessons = lessons.items.some(lesson => {
                    const updatedAt = new Date(lesson.updated_at || lesson.created_at || 0).getTime();
                    return Date.now() - updatedAt < this.STALE_THRESHOLD;
                });
                if (!hasRecentLessons) {
                    result.staleData.push({
                        isStale: true,
                        lastSync,
                        clientTimestamp: Date.now(),
                        dataType: 'lessons',
                        needsRefresh: true
                    });
                }
            }
        }
        catch (error) {
            result.errors.push(`Lessons validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Validate notes data
     */
    async validateNotesData(state, result) {
        try {
            const notes = state.notes;
            if (!notes.items || notes.items.length === 0)
                return;
            const lastSync = this.getLastSyncTime('notes');
            const isStale = Date.now() - lastSync > this.STALE_THRESHOLD;
            if (isStale) {
                // Check if we have recent notes
                const hasRecentNotes = notes.items.some(note => {
                    const updatedAt = new Date(note.updated_at || note.created_at || 0).getTime();
                    return Date.now() - updatedAt < this.STALE_THRESHOLD;
                });
                if (!hasRecentNotes) {
                    result.staleData.push({
                        isStale: true,
                        lastSync,
                        clientTimestamp: Date.now(),
                        dataType: 'notes',
                        needsRefresh: true
                    });
                }
            }
        }
        catch (error) {
            result.errors.push(`Notes validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Validate quiz data
     */
    async validateQuizData(state, result) {
        try {
            const quiz = state.quiz;
            if (!quiz.templates || quiz.templates.length === 0)
                return;
            const lastSync = this.getLastSyncTime('quiz');
            const isStale = Date.now() - lastSync > this.STALE_THRESHOLD;
            if (isStale) {
                result.staleData.push({
                    isStale: true,
                    lastSync,
                    clientTimestamp: Date.now(),
                    dataType: 'quiz',
                    needsRefresh: true
                });
            }
        }
        catch (error) {
            result.errors.push(`Quiz validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Validate schedule data
     */
    async validateScheduleData(state, result) {
        try {
            const schedule = state.schedule;
            if (!schedule)
                return;
            const lastSync = this.getLastSyncTime('schedule');
            const isStale = Date.now() - lastSync > this.STALE_THRESHOLD;
            if (isStale) {
                result.staleData.push({
                    isStale: true,
                    lastSync,
                    clientTimestamp: Date.now(),
                    dataType: 'schedule',
                    needsRefresh: true
                });
            }
        }
        catch (error) {
            result.errors.push(`Schedule validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Get server timestamp for a resource
     */
    async getServerTimestamp(endpoint) {
        try {
            const response = await axiosInstance.head(endpoint);
            const lastModified = response.headers['last-modified'];
            if (lastModified) {
                return new Date(lastModified).getTime();
            }
        }
        catch (error) {
            console.warn(`Failed to get server timestamp for ${endpoint}:`, error);
        }
        return undefined;
    }
    /**
     * Get last sync time for a data type
     */
    getLastSyncTime(dataType) {
        const status = this.syncStatus.get(dataType);
        return status?.lastSync || 0;
    }
    /**
     * Update sync time for a data type
     */
    updateSyncTime(dataType) {
        const currentStatus = this.syncStatus.get(dataType) || {
            isStale: false,
            lastSync: 0,
            clientTimestamp: Date.now(),
            dataType,
            needsRefresh: false
        };
        this.syncStatus.set(dataType, {
            ...currentStatus,
            lastSync: Date.now(),
            isStale: false,
            needsRefresh: false
        });
    }
    /**
     * Mark data as stale
     */
    markAsStale(dataType) {
        const currentStatus = this.syncStatus.get(dataType) || {
            isStale: false,
            lastSync: 0,
            clientTimestamp: Date.now(),
            dataType,
            needsRefresh: false
        };
        this.syncStatus.set(dataType, {
            ...currentStatus,
            isStale: true,
            needsRefresh: true
        });
    }
    /**
     * Notify listeners that a data type is stale (so they can trigger refetch).
     * Call this after markAsStale when you want immediate refetch (e.g. from middleware).
     * If no listeners are subscribed (e.g. PersistentDataProvider unmounted), the stale
     * type is queued and flushed when the next listener is added.
     */
    notifyStale(dataType) {
        this.markAsStale(dataType);
        if (this.listeners.length === 0) {
            this.queuedStaleDataTypes.add(dataType);
            return;
        }
        const result = {
            isValid: true,
            staleData: [{
                    isStale: true,
                    lastSync: 0,
                    clientTimestamp: Date.now(),
                    dataType,
                    needsRefresh: true
                }],
            errors: [],
            warnings: []
        };
        this.listeners.forEach(listener => listener(result));
    }
    /**
     * Returns data types currently marked stale with needsRefresh (including queued).
     * Used by PersistentDataProvider on mount to force refetch when a write occurred while unmounted.
     */
    getStaleTypesNeedingRefresh() {
        const fromQueue = Array.from(this.queuedStaleDataTypes);
        const fromStatus = Array.from(this.syncStatus.entries())
            .filter(([, s]) => s.needsRefresh)
            .map(([dataType]) => dataType);
        const combined = new Set([...fromQueue, ...fromStatus]);
        return Array.from(combined);
    }
    /**
     * Get sync status for a data type
     */
    getSyncStatus(dataType) {
        return this.syncStatus.get(dataType);
    }
    /**
     * Get all sync statuses
     */
    getAllSyncStatuses() {
        return new Map(this.syncStatus);
    }
    /**
     * Add listener for validation results.
     * If there are queued stale types (notifications that arrived while no subscriber was listening),
     * flushes them to this listener immediately so the provider can trigger refetches on mount.
     */
    addListener(listener) {
        this.listeners.push(listener);
        if (this.queuedStaleDataTypes.size > 0) {
            const staleData = Array.from(this.queuedStaleDataTypes).map((dataType) => ({
                isStale: true,
                lastSync: 0,
                clientTimestamp: Date.now(),
                dataType,
                needsRefresh: true
            }));
            const result = {
                isValid: true,
                staleData,
                errors: [],
                warnings: []
            };
            listener(result);
            this.queuedStaleDataTypes.clear();
        }
    }
    /**
     * Remove listener
     */
    removeListener(listener) {
        const index = this.listeners.indexOf(listener);
        if (index >= 0) {
            this.listeners.splice(index, 1);
        }
    }
    /**
     * Force validation of specific data type
     */
    async validateDataType(dataType) {
        try {
            const state = store.getState();
            const result = {
                isValid: true,
                staleData: [],
                errors: [],
                warnings: []
            };
            switch (dataType) {
                case 'auth':
                    await this.validateAuthData(state, result);
                    break;
                case 'lessons':
                    await this.validateLessonsData(state, result);
                    break;
                case 'notes':
                    await this.validateNotesData(state, result);
                    break;
                case 'quiz':
                    await this.validateQuizData(state, result);
                    break;
                case 'schedule':
                    await this.validateScheduleData(state, result);
                    break;
                default:
                    console.warn(`Unknown data type: ${dataType}`);
                    return null;
            }
            const status = result.staleData.find(s => s.dataType === dataType);
            if (status) {
                this.syncStatus.set(dataType, status);
            }
            return status || null;
        }
        catch (error) {
            console.error(`Failed to validate ${dataType}:`, error);
            return null;
        }
    }
    /**
     * Clear all sync statuses
     */
    clearSyncStatuses() {
        this.syncStatus.clear();
    }
}
// Export singleton instance
export const dataSyncValidator = DataSyncValidator.getInstance();
// Export class for testing
export { DataSyncValidator };
// Auto-start validation in development
if (process.env.NODE_ENV === 'development') {
    dataSyncValidator.startValidation();
}
